/** The BACKEND CONTRACT the lifted chrome calls — and what pdcad answers instead.
 *
 * ⚠⚠ **THE ORIGINAL RULE HERE WAS "EVERY STUB THROWS", AND IT WAS RIGHT UNTIL IT WASN'T.** It
 * was written while the chrome had no host: a stub returning `[]` would have given a shell that
 * looked like it was working against an empty drawing, and the first person to try it would have
 * concluded the app was broken rather than unbuilt. Correct then. It became wrong the moment the
 * studio mounted, for a reason that is worth writing down rather than rediscovering:
 *
 * `shell.tsx` calls `designSheets(planId).then(...)` in an effect the moment a drawing opens. A
 * **synchronous** throw there is not caught by `.then`, so it escaped the effect and React
 * unmounted the whole studio. The screenshot was an AutoCAD window with a stuck "Opening…"
 * toast and no canvas — which reads exactly like a hung load, not like an unbuilt endpoint. The
 * rule meant to prevent a misleading UI had produced one.
 *
 * ⇒ So the stubs are now split by DIRECTION, which is the distinction that actually matters:
 *
 * * **A READ resolves EMPTY, and that is the truth, not a placation.** pdcad genuinely has no
 *   sheet sets, no lights, no surfaces and no parametric constraints. `{pages: []}` is an
 *   accurate answer to "what sheets does this drawing have", not a pretend one.
 * * **A WRITE REJECTS, and rejects rather than throwing.** Nothing may silently appear to save.
 *   A rejected promise reaches the `.catch` the caller already has and reports; a synchronous
 *   throw takes the page down instead, which is how this was found.
 *
 * ⚠ The four endpoints pdcad really has are wired to `studio/api.ts`. When a fifth is built, it
 * moves from `soon` to `real` here — never the reverse.
 */

/** ⚠ REJECTS, does not throw. See the header — a synchronous throw inside `.then` chains in
 *  `shell.tsx` unmounted the studio. */
// ⚠ `Promise<any>`, NOT `Promise<never>`. `never` looks tidier and is wrong: `await` on it
// gives `never`, so every `r.block` / `r.report` at 30 call sites in `shell.tsx` becomes
// "Property does not exist on type 'never'" — 40 type errors caused entirely by the stub.
const soon = (name: string) => (..._a: any[]): Promise<any> =>
  Promise.reject(new Error(
    `${name} is not built in pdcad yet. The chrome is lifted; this endpoint is not. ` +
    `See /opt/cad/docs/BUILD-PLAN.md for the order things are being built in.`));

/** A read of something pdcad genuinely does not have. ⚠ Empty is the honest answer here. */
const none = (shape: any) => (..._a: any[]): Promise<any> => Promise.resolve(shape);

/** ⚠⚠ A PROXY, not a bare function, and the type error that forced it is worth keeping.
 *  Several of these are RESOURCE OBJECTS in the old app — `designPlansApi.update(...)`,
 *  `.list(...)`, `.get(...)` — so a stub declared as a plain function typechecks at the import
 *  and fails at `.update`, which is four screens away from the cause. A callable Proxy answers
 *  any property with another stub, so the shape is right whatever the call site expects and the
 *  error names the endpoint rather than the property. */
const resource = (name: string, reads: Record<string, any> = {}): any =>
  new Proxy(function () {} as any, {
    get: (_t, prop) => (typeof prop === "string"
      ? (prop in reads ? reads[prop] : resource(`${name}.${prop}`, reads))
      : undefined),
    apply: () => soon(name)(),
  });

// ── reads: pdcad has none of these, and says so with an empty answer ────────────────────────
export const designSheets = none({ pages: [] });
export const designLights = none({ lights: [] });
export const designSurfaces = none({ surfaces: [], report: null });
export const designBlockEdit = none(null);
export const designBlockAttrs = none({ defs: [], values: [] });

// ── writes: refused, loudly, and never silently ─────────────────────────────────────────────
export const designAddBody = soon("ADDBODY");
export const designAddLight = soon("LIGHT");
export const designAddSurface = soon("SURFACE");
export const designBlockEditOp = soon("BEDIT");
export const designDelLight = soon("LIGHTDEL");
export const designDelSurface = soon("SURFACEDEL");
export const designInterfere = soon("INTERFERE");
export const designMakeBlock = soon("BLOCK");
export const designMeshSolid = soon("MESHSMOOTH");
export const designPdfImport = soon("PDFIMPORT");
export const designPdfProbe = soon("PDFIMPORT");
export const designSetBlockAttrs = soon("BATTMAN");
export const designSolidOpAdd = soon("UNION/SUBTRACT/INTERSECT");

// ── the four pdcad really answers ────────────────────────────────────────────────────────────
import { exportLayout, importFile, listDrawings, loadDisplay, loadLayers, parametric,
         patchLayerRow } from "../studio/api";

/** ⚠⚠ **THESE THREE MOVED FROM `soon` TO REAL, WHICH IS THE ONLY DIRECTION THEY MOVE.**
 *  The dimensional half of the Parametric tab is built now — `Constraint` (migration 0010),
 *  `backend/drawing/parametric.py`, one `parametric` action. The header's rule holds: a stub is
 *  replaced when the endpoint exists, never the reverse.
 *  ⚠ `designParametric` was a `none({params: []})` read and is now the real GET, so the
 *  Parameters Manager stops opening on a permanently empty grid. */
export const designParametric = (planId: number) => parametric(planId, "GET");
export const designParametricPost = (planId: number, body: any) =>
  parametric(planId, "POST", body);
export const designParametricDel = (planId: number, query: string) =>
  parametric(planId, "DELETE", undefined, query);
/** ⚠ EXPORTLAYOUT. Was a `soon` stub carrying `ON_SHEET` — "a named sheet SET is not built" —
 *  which is the answer to a question EXPORTLAYOUT does not ask. See the 2026-09-04 audit. */
export const designExportLayout = (planId: number, space?: string) =>
  exportLayout(planId, space);

/** ⚠ `list`/`get` are real; anything that WRITES a drawing record is not built. */
export const designPlansApi = resource("PLANS", {
  list: () => listDrawings(),
  get: (id: number) => loadDisplay(id),
});

export const designLayersApi = resource("LAYERS", {
  list: (planId: number) => loadLayers(planId),
  // ⚠⚠ **`visible` IS THE OLD APP'S NAME FOR IT AND `on` IS DXF'S.** Every layer command in the
  // lifted chrome — LAYON, LAYOFF, LAYISO, LAYLCK, LAYULK, LAYUNISO — patches `{visible}` or
  // `{locked}`, so the translation belongs HERE, once, rather than in six call sites inside a
  // 10,000-line file this app does not own. Anything else is passed straight through.
  update: (layerId: number, patch: any) => {
    const { visible, ...rest } = patch || {};
    return patchLayerRow(layerId,
                         visible === undefined ? rest : { ...rest, on: !!visible });
  },
});

/** ⚠ DXF only, and it says which one it refused. `docs/GAP.md` §9: LibreDWG *can* write DWG and
 *  pdcad does not ship that until something other than LibreDWG has confirmed one opens. */
// ⚠ The extra arguments are the old app's plot options and download name. They are accepted
// and ignored rather than dropped from the signature: the call sites pass four, and narrowing
// the signature would make the CALLER wrong about an endpoint that is fine.
export const designExport = async (planId: number, fmt: string,
                                   opts?: any, name?: string): Promise<any> => {
  if (fmt === "dxf") { window.location.href = `/api/drawings/${planId}/export/`; return; }
  if (fmt === "pdf") {
    // ⚠⚠ **THE PLOT DIALOG'S OWN FIELDS, MAPPED ONCE.** `shell.tsx` builds `{sheet,
    // orientation, scale, colour, …}` from AutoCAD's Plot dialog; the endpoint speaks `page`,
    // `landscape`, `scale`, `mono`. Translating here means the 10,000-line chrome keeps its own
    // vocabulary and the API keeps DXF's.
    const q = new URLSearchParams({
      page: String(opts?.sheet ?? "A3"),
      // ⚠ "auto" goes THROUGH to the server, which knows the drawing's shape. Resolving it here
      // would guess landscape and lay every tall stair section on its side.
      landscape: opts?.orientation === "portrait" ? "0"
               : opts?.orientation === "landscape" ? "1" : "auto",
      scale: opts?.scale === "auto" || !opts?.scale ? "0" : String(opts.scale),
      mono: opts?.colour ? "0" : "1",
    });
    // ⚠ Only when there IS one. `?space=` empty would ask the server for a layout called "",
    // and its answer to that is a 422 rather than model space.
    if (opts?.space) q.set("space", String(opts.space));
    const url = `/api/drawings/${planId}/plot/?${q}`;
    // ⚠ A NAME means Plot and no name means Preview — AutoCAD's own distinction, and the same
    // request either way. `shell.tsx` relies on exactly this.
    if (name) { q.set("download", "1"); window.location.href = `/api/drawings/${planId}/plot/?${q}`; }
    else window.open(url, "_blank");
    return;
  }
  throw new Error(`pdcad plots to PDF and exports DXF — "${fmt}" is not built yet.`);
};

// ⚠ `opts` is the old app's import options — units, layer prefix, xref handling. Accepted and
// ignored for now, and NOT dropped from the signature: `AcadImport` passes three arguments.
export const designImportCad = (_planId: number, file: File, _opts?: any) => importFile(file);
